// src/pages/admin/AdminMarketing.js
// Email marketing : envoyer des offres aux segments (particuliers / partenaires / agences / tous)
// Maxula

import React, { useEffect, useState } from 'react';
import { FiMail, FiSend, FiUsers, FiAlertTriangle, FiCheckCircle, FiXCircle } from 'react-icons/fi';
import { toast } from 'react-toastify';
import sanitizeHtml from '../../utils/sanitizeHtml';
import { marketingAPI } from '../../services/api';

const SEGMENT_LABELS = {
  all: 'Tous les inscrits',
  clients: 'Clients',
  merchants: 'Tous les marchands',
  particulier: 'Particuliers / Marchands',
  promoteur: 'Promoteurs immobiliers',
  agence: 'Agences'
};

const AdminMarketing = () => {
  const [counts, setCounts] = useState({});
  const [smtpOk, setSmtpOk] = useState(true);
  const [segment, setSegment] = useState('all');
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadAll = async () => {
    try {
      const [aud, camp] = await Promise.all([marketingAPI.audience(), marketingAPI.listCampaigns()]);
      const d = aud.data?.data || {};
      setCounts(d.counts || {});
      setSmtpOk(!!d.smtpConfigured);
      setCampaigns(camp.data?.data || []);
    } catch (e) {
      toast.error('Erreur de chargement');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadAll(); }, []);

  const send = async () => {
    if (!subject.trim() || !body.trim()) return toast.error('Sujet et message obligatoires');
    if (!window.confirm(`Envoyer à « ${SEGMENT_LABELS[segment]} » (${counts[segment] || 0} destinataire(s)) ?`)) return;
    try {
      setSending(true);
      const res = await marketingAPI.sendCampaign({ subject, body, segment });
      toast.success(res.data?.message || 'Campagne envoyée');
      setSubject(''); setBody('');
      loadAll();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Erreur lors de l\'envoi');
    } finally { setSending(false); }
  };

  if (loading) return <div className="flex justify-center py-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600" /></div>;

  return (
    <div className="p-6 max-w-5xl">
      <h1 className="text-3xl font-bold text-gray-900 mb-1 flex items-center gap-2"><FiMail /> Email Marketing</h1>
      <p className="text-gray-500 mb-6">Envoyez vos offres par email à un segment de votre audience.</p>

      {!smtpOk && (
        <div className="mb-6 rounded-xl border border-amber-300 bg-amber-50 p-4 text-amber-800 text-sm flex gap-3">
          <FiAlertTriangle className="shrink-0 mt-0.5" />
          <div>
            <strong>SMTP non configuré.</strong> Pour envoyer réellement des emails, renseignez <code>EMAIL_HOST</code>, <code>EMAIL_PORT</code>, <code>EMAIL_USER</code> et <code>EMAIL_PASS</code> dans le fichier <code>.env</code> du serveur, puis redémarrez. Vous pouvez préparer la campagne en attendant.
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Composer */}
        <div className="lg:col-span-2 bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-4">Nouvelle campagne</h2>

          <label className="block text-sm font-medium mb-1">Segment</label>
          <select value={segment} onChange={(e) => setSegment(e.target.value)} className="w-full border rounded-lg px-3 py-2 mb-1">
            {Object.keys(SEGMENT_LABELS).map((s) => (
              <option key={s} value={s}>{SEGMENT_LABELS[s]} ({counts[s] || 0})</option>
            ))}
          </select>
          <div className="text-xs text-gray-500 mb-4 flex items-center gap-1"><FiUsers size={12} /> {counts[segment] || 0} destinataire(s)</div>

          <label className="block text-sm font-medium mb-1">Sujet</label>
          <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Ex : 🎉 Nouvelles offres exclusives cette semaine !" className="w-full border rounded-lg px-3 py-2 mb-4" />

          <label className="block text-sm font-medium mb-1">Message (HTML autorisé)</label>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={9} placeholder="<h2>Bonjour 👋</h2><p>Découvrez nos meilleures offres...</p>" className="w-full border rounded-lg px-3 py-2 mb-2 font-mono text-sm" />
          <p className="text-xs text-gray-400 mb-4">Astuce : vous pouvez utiliser des balises HTML simples (&lt;h2&gt;, &lt;p&gt;, &lt;a href&gt;, &lt;strong&gt;). L'en-tête Maxula et le pied de page sont ajoutés automatiquement.</p>

          <button onClick={send} disabled={sending} className="px-5 py-2.5 rounded-lg bg-primary-600 text-white font-semibold hover:bg-primary-700 disabled:opacity-60 flex items-center gap-2">
            <FiSend size={16} /> {sending ? 'Envoi en cours…' : 'Envoyer la campagne'}
          </button>
        </div>

        {/* Aperçu */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="font-semibold text-gray-900 mb-3">Aperçu</h2>
          <div className="border rounded-lg overflow-hidden text-sm">
            <div className="bg-primary-600 text-white px-3 py-2 font-bold">Maxula</div>
            <div className="p-3 text-gray-700 min-h-[120px]" dangerouslySetInnerHTML={{ __html: sanitizeHtml(body || '<span class="text-gray-300">Votre message apparaîtra ici…</span>') }} />
            <div className="bg-gray-900 text-gray-400 px-3 py-2 text-[10px]">Maxula — © {new Date().getFullYear()}</div>
          </div>
        </div>
      </div>

      {/* Historique */}
      <div className="bg-white rounded-xl border border-gray-200 p-5 mt-6">
        <h2 className="font-semibold text-gray-900 mb-4">Campagnes envoyées</h2>
        {campaigns.length === 0 ? (
          <div className="text-center text-gray-400 py-8">Aucune campagne pour le moment.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-gray-500 border-b">
                  <th className="py-2 pr-4">Sujet</th>
                  <th className="py-2 pr-4">Segment</th>
                  <th className="py-2 pr-4">Destinataires</th>
                  <th className="py-2 pr-4">Envoyés</th>
                  <th className="py-2 pr-4">Statut</th>
                  <th className="py-2 pr-4">Date</th>
                </tr>
              </thead>
              <tbody>
                {campaigns.map((c) => (
                  <tr key={c._id} className="border-b last:border-0">
                    <td className="py-2 pr-4 font-medium text-gray-900 max-w-[220px] truncate">{c.subject}</td>
                    <td className="py-2 pr-4">{SEGMENT_LABELS[c.segment] || c.segment}</td>
                    <td className="py-2 pr-4">{c.recipientsCount}</td>
                    <td className="py-2 pr-4">{c.sentCount}{c.failedCount ? ` (${c.failedCount} échec)` : ''}</td>
                    <td className="py-2 pr-4">
                      {c.status === 'sent' ? <span className="inline-flex items-center gap-1 text-green-600"><FiCheckCircle size={13} /> Envoyée</span>
                        : c.status === 'failed' ? <span className="inline-flex items-center gap-1 text-red-500" title={c.error}><FiXCircle size={13} /> Échec</span>
                        : <span className="text-amber-500">{c.status}</span>}
                    </td>
                    <td className="py-2 pr-4 text-gray-500">{c.sentAt ? new Date(c.sentAt).toLocaleString('fr-FR') : new Date(c.createdAt).toLocaleDateString('fr-FR')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminMarketing;
